import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const menuItems = [
    { label: 'Home', id: 'hero' },
    { label: 'Rates', id: 'rates' },
    { label: 'About Us', id: 'about' },
    { label: 'How It Works', id: 'process' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' }
  ];

  const languages = [
    { code: 'en', name: '', flag: 'en' },
    { code: 'pl', name: '', flag: 'pl' }
  ];

  const switchLanguage = (langCode) => {
    if (window.Weglot) {
      window.Weglot.switchTo(langCode);
      setCurrentLang(langCode);
      setLangMenuOpen(false);
    }
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLang) || languages[0];
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-white/10 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <img 
              src="/custodiafxlogo.png" 
              alt="CustodiaFX Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className={`text-xl font-bold ${isScrolled ? 'text-blue-900' : 'text-white'}`}>
              Custodia<span className="text-green-400">FX</span>
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium transition-colors ${
                  isScrolled 
                    ? 'text-blue-900 hover:text-green-600' 
                    : 'text-white hover:text-green-400'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className={`flex items-center px-2 py-1.5 rounded-md font-medium transition-all duration-200 text-sm ${
                  isScrolled 
                    ? 'text-blue-900 hover:bg-blue-50 hover:text-green-600' 
                    : 'text-white hover:bg-white/10 hover:text-green-400'
                }`}
              >
                <div className="w-6 h-4 rounded-sm border border-gray-300 overflow-hidden">
                  <img 
                    src={`https://cdn.weglot.com/images/flags/${getCurrentLanguage().flag}.svg`}
                    alt={getCurrentLanguage().code}
                    className="w-full h-full object-cover"
                  />
                </div>
              </button>
              
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[50px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`flex items-center justify-center w-full px-3 py-2 text-center transition-colors ${
                        currentLang === lang.code 
                          ? 'bg-green-50 text-green-700' 
                          : 'text-blue-900 hover:bg-slate-50'
                      }`}
                    >
                      <div className="w-6 h-4 rounded-sm border border-gray-300 overflow-hidden">
                        <img 
                          src={`https://cdn.weglot.com/images/flags/${lang.flag}.svg`}
                          alt={lang.code}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className={isScrolled ? 'text-blue-900' : 'text-white'}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white rounded-lg shadow-lg mx-4 mt-2 py-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-4 py-2 text-blue-900 hover:bg-slate-50 hover:text-green-600 transition-colors"
              >
                {item.label}
              </button>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="border-t mt-2 pt-2">
              <div className="px-4 py-2 text-gray-500 text-sm font-medium">Language</div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => switchLanguage(lang.code)}
                  className={`flex items-center w-full px-4 py-2 text-left transition-colors ${
                    currentLang === lang.code 
                      ? 'bg-green-50 text-green-700' 
                      : 'text-blue-900 hover:bg-slate-50'
                  }`}
                >
                  <div className="w-6 h-4 rounded-sm border border-gray-300 overflow-hidden">
                    <img 
                      src={`https://cdn.weglot.com/images/flags/${lang.flag}.svg`}
                      alt={lang.code}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}